// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { defer, Observable, of } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { UndoRedoUtils } from '@shared/undo/undo-redo-utils';
import { ProjectModel } from '@project/model/project.model';
import { ArrayUtils } from '@shared/util/array-utils';
import { ReplaceBusbarArticleCreator } from './replace-busbar-article.creator';
import { ReplaceBusbarDataBuilder } from './replace-busbar-data-builder';
import { Busbar } from './replace-busbar-data';
import { ArticleLineHolder } from '../../article-line-holder';
import { ConnectionAreaVO, MetaElementUsageIdentityVO } from '@shared/vo/meta-element.vo';
import { Connection } from '@3d-scene/model3d/connection';
import { IOperation } from '@shared/undo/operation/i-operation';
import { Dictionary } from 'typescript-collections';
import { Vector3 } from 'three';
import { InsertArticleHelper } from '../../insert-article.helper';
import { ArticleManager } from '../../../manager/article.manager';
import { BusbarArticleConnectProcessor } from '@project/components/article/processor/prepare/connect/busbar-article-connect.processor';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ProjectChangedType } from '@project/model/project-changed';
import { InsertArticleHelperFactory } from '../../insert-article-helper.factory';
import { ArticleUsageUtils } from '../../../util/article-usage-utils';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { ProjectMetaElementService } from '@project/manager/project-meta-element.service';
import { DetachArticleUsageService } from '@project/service/detach-article-usage.service';
import { ProjectArticleService } from '@project/service/project-article.service';
import { ConnectionInfoService } from '@3d-scene/model3d/service/connection-info.service';
import { ReplaceBusbarService } from './replace-busbar.service';
import { ConnectionInfoUtils } from '@3d-scene/model3d/util/connection-info-utils';

@Injectable({providedIn: 'root'})
export class ReplaceBusbarArticleHelper {

  private _pendingBusbars: Map<string, Busbar> = new Map<string, Busbar>();

  constructor(private projectModel: ProjectModel,
              private articleManager: ArticleManager,
              private busbarArticleConnectProcessor: BusbarArticleConnectProcessor,
              private insertArticleHelperFactory: InsertArticleHelperFactory,
              private undoRedoUtils: UndoRedoUtils,
              private articleLineHolder: ArticleLineHolder,
              private containerService: ContainerService,
              private projectMetaElementService: ProjectMetaElementService,
              private detachArticleUsageService: DetachArticleUsageService,
              private projectArticleService: ProjectArticleService,
              private connectionInfoService: ConnectionInfoService,
              private replaceBusbarService: ReplaceBusbarService) {
    this.projectModel.projectChanged$
      .pipe(takeUntilDestroyed(), filter(change => change.type === ProjectChangedType.ProjectCleared))
      .subscribe(() => this.clear());
  }

  public get detachOperations(): IOperation[] {
    const operations: IOperation[] = [];
    for (const busbar of this._pendingBusbars.values()) {
      if (busbar.detachOperation) {
        operations.push(busbar.detachOperation);
      }
    }
    return operations;
  }

  public detachBusbarsContainersUnchecked(busbarContainers: ArticleContainer[]): Promise<void> {
    return this.detachBusbarContainers(busbarContainers, null, null);
  }

  public async detachBusbarContainers(busbarContainers: ArticleContainer[], moveMap: Dictionary<string, Vector3>, insertHelper: InsertArticleHelper): Promise<void> {
    if (ArrayUtils.isEmpty(busbarContainers)) {
      return;
    }

    const busbarsToDetach: Busbar[] = [];
    for (const busbarContainer of busbarContainers) {
      if (this.replaceBusbarService.isMoveMapContainsAllBackwardUsageIdsWithSameOffset(busbarContainer, moveMap, insertHelper)) {
        continue;
      }
      busbarsToDetach.push(this.createBusbar(busbarContainer));
    }
    await this.detachBusbars(busbarsToDetach);
  }

  public detachBusbars(busbars: Busbar[]): Promise<void> {
    for (const busbar of busbars) {
      this.storeBusbar(busbar);

      const busbarContainer: ArticleContainer = this.containerService.findArticleContainerInGlobal(busbar.originalUsageId);
      busbar.connectedUsageIds.forEach((usageId: MetaElementUsageIdentityVO) => {
        const connectedContainer: ArticleContainer = this.containerService.findArticleContainerInGlobal(usageId.articleUsageId);
        // remove association only for busbar at this point
        // association for connected article(s) will be removed later on in time when it gets reconnected to re-created busbar
        // otherwise move-map of connected articles will not be calculated correctly
        ArticleUsageUtils.removeAssociationsWithUsage(busbarContainer.articleUsage, connectedContainer.articleGUID);
      });
      this.articleLineHolder.clearArticleLineCacheByGUIDs(busbar.connectedArticleUsageIds);

      busbar.detachOperation = this.undoRedoUtils.createUndoDetachBusbarOperationByArticleContainers(busbarContainer);
    }
    return this.detachArticleContainersFromProject(busbars.map((busbar) => busbar.originalUsageId));
  }

  private async detachArticleContainersFromProject(usageIdsToDetach: string[]): Promise<void> {
    if (usageIdsToDetach.length) {
      await this.detachArticleUsageService.detachArticleUsageFromProjectByIds(usageIdsToDetach);
    }
  }

  private storeBusbar(busbar: Busbar): void {
    this._pendingBusbars.set(busbar.originalUsageId, busbar);
  }

  public mapBusbarContainers(busbarContainers: ArticleContainer[]): Busbar[] {
    return busbarContainers.map((container) => this.createBusbar(container));
  }

  private createBusbar(busbarContainer: ArticleContainer): Busbar {
    return ReplaceBusbarDataBuilder.buildBusbar(busbarContainer, this.insertArticleHelperFactory.createInstanceByRangeOnLineArticleContainer(busbarContainer));
  }

  public get pendingBusbars(): Busbar[] {
    return Array.from(this._pendingBusbars.values());
  }

  public createPendingBusbarArticles(): Observable<IOperation>[] {
    const busbarsToCreate: Busbar[] = this.pendingBusbars;
    this._pendingBusbars.clear();
    return busbarsToCreate.map((busbar) => this.createPendingBusbar(busbar));
  }

  private createPendingBusbar(busbarToCreate: Busbar): Observable<IOperation> {
    return this.createBusbarContainer(busbarToCreate).pipe(
      map((busbarContainer: ArticleContainer) => {
        const attachOperation = this.undoRedoUtils.createUndoAttachBusbarOperationByArticleContainers(busbarContainer);
        attachOperation.linkOperation(busbarToCreate.detachOperation);
        // restore connection only after attach operation is created, otherwise duplicated information in BaseArticleOperation will be stored
        this.restoreBusbarGivingConnections(busbarToCreate, busbarContainer);
        return attachOperation;
      }),
    );
  }

  private restoreBusbarGivingConnections(sourceBusbar: Busbar, createdBusbarContainer: ArticleContainer): void {
    if (!createdBusbarContainer) {
      return;
    }
    // assumed busbar has only range-on-line connection
    const rangeOnLineConnectionArea: ConnectionAreaVO = ConnectionInfoUtils.findDefaultRangeOnLineConnection(createdBusbarContainer.article.metaElements);
    const acceptingConnection: Connection = createdBusbarContainer.findFirstConnectionByName(rangeOnLineConnectionArea.name);
    sourceBusbar.connectedUsageIds.forEach((connectedId) => {
      const connectedContainer: ArticleContainer = this.containerService.findArticleContainerInGlobal(connectedId.articleUsageId);
      const connectedContainerConnection: Connection = connectedContainer.findConnection(connectedId.metaElementName);
      ArticleUsageUtils.removeAssociationsWithUsage(connectedContainer.articleUsage, sourceBusbar.originalUsageId);
      connectedContainerConnection.metaElementUsage = null;
      const givingForwardConnection: Connection = this.connectionInfoService.findConnectionByIdentity(connectedId);
      this.projectMetaElementService.createUsageBetweenConnections(givingForwardConnection, acceptingConnection);
    });
  }

  private createBusbarContainer(busbar: Busbar): Observable<ArticleContainer | undefined> {
    if (!busbar) {
      return of(undefined);
    }

    const articleCreator: ReplaceBusbarArticleCreator =
      new ReplaceBusbarArticleCreator(
        this.projectModel,
        this.articleManager,
        this.busbarArticleConnectProcessor,
        this.projectArticleService,
        this.connectionInfoService);
    return defer(async () => (await articleCreator.createBusbarArticles([busbar])).at(0));
  }

  public clear(): void {
    this._pendingBusbars.clear();
  }
}
